import request from '@/utils/request';
import type { R, RL, RP, ApiResult } from '@/api';
import { RParam } from '@/api';
import type { TenantTeacher, TeantTeacherCopy, TeacherInfo } from './model';
import fileUtil from '@/utils/file-util';
import LzdUtil, {
  getAppId,
  getDataScopeId,
  getRoleCode,
  JSONUtil
} from '@/utils/lzd-util';
import LzdAjax from '@/utils/lzd-ajax';

const BASE_URL = '/bsl/tenant/teachers';
/**
 * 分页查询用户
 */
export async function pageTenantTeacher(params: RP) {
  params = params ?? { where: [] };
  params.where = params.where ?? [];
  return LzdAjax.post<RL<TenantTeacher>>(
    `${BASE_URL}/query/page`,
    params,
    false,
    true
  ).then((v) => v.data ?? { count: 0, list: [] });
}

/**
 * 查询全部用户
 */
export async function listAllTenantTeacher(params: RP) {
  params.where = params.where ?? [];
  const res = await request.post<R<TenantTeacher[]>>(
    `${BASE_URL}/query/list`,
    params
  );
  return res.data.code === 0 ? res.data.data : Promise.reject(res.data);
}

/**
 * 查询志愿老师数据
 */
export async function pageVolunteerTenantTeacher(params: RP) {
  const res = await request.post<R<RL<TenantTeacher>>>(
    `${BASE_URL}/volunteer/page`,
    params
  );
  return res.data.code === 0 ? res.data.data : Promise.reject(res.data);
}

/**
 * 查询单个教师
 */
export async function listOneTenantTeacher(params: RP) {
  params = params ?? {};
  params.where = params.where ?? [];
  return LzdAjax.post<TenantTeacher>(
    `${BASE_URL}/query/single`,
    params,
    false,
    true
  ).then((v) => v.data);
}

/**
 * 保存信息
 */
export async function saveTenantTeacher(data: TenantTeacher) {
  const formData = new FormData();
  data.dataScopeId = getDataScopeId();
  formData.append('tenantTeacherJson', JSON.stringify(data));
  formData.append('password', data.password as string);
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/insert`,
    formData
  );
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 修改信息
 */
export async function updateTenantTeacher(data: TenantTeacher) {
  const res = await request.post<ApiResult<unknown>>(`${BASE_URL}/update`, {
    //字段修改
    update: RParam.buildEdit(data, (k: string) => k !== 'seq'),
    //修改条件
    where: [RParam.eq('seq', data.seq), RParam.eq('password', data.password)],
    tenantId: data.tenantId,
    dataScopeId: data.dataScopeId
  });
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 修改信息
 */
export async function updateTenantTeacherStudentsSize(data: RP) {
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/update/students/size`,
    data
  );
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 批量删除用户信息
 */
export async function removeTenantTeacher(
  data: (number | undefined)[],
  tenantId: string,
  dataScopeId: number
) {
  const param: RP = {
    where: RParam.buildWhere({ seq: data }),
    tenantId: tenantId,
    dataScopeId: dataScopeId
  };
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/batch/delete`,
    param
  );
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 导入老师数据
 */
export async function importTenantTeacher(param: RP, file) {
  const formData = new FormData();
  param.appId = getAppId();
  param.roleCode = getRoleCode();
  param.dataScopeId = getDataScopeId();
  formData.append('param', JSONUtil.toStr(param));
  formData.append('file', file);
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/import`,
    formData
  );
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 导出导师信息
 */
export async function exportTenantTeacher(param: RP) {
  fileUtil.downloadPostFile(`${BASE_URL}/export`, {
    param: JSONUtil.toStr(param)
  });
}

/**
 * 重置导师密码
 * @param userid  账号
 * @param password 密码
 * @param tenantId 机构编码
 */
export async function resetStudentPassword(
  userid: string,
  password: string,
  tenantId: string,
  dataScopeId: number
) {
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/reset/password`,
    {
      update: RParam.buildEdit({ password }),
      where: [RParam.eq('userid', userid)],
      tenantId: tenantId,
      dataScopeId: dataScopeId
    }
  );
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 批量重置导师用户密码
 */
export async function batchResetPassword(params: RP) {
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/batch/reset/password`,
    params
  );
  return res.data;
}

/**导师复制 */
export async function copyAdvisor(data: TeantTeacherCopy) {
  const res = await request.post<ApiResult<unknown>>(`${BASE_URL}/copy`, data);
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 查询导师信息
 */
export async function queryTeacherSelectList(param: RP) {
  const res = await request.post<ApiResult<TeacherInfo[]>>(
    `${BASE_URL}/select-list`,
    param
  );

  if (res.data.code === 0) {
    return res.data.data;
  }

  return Promise.reject(new Error(res.data.message));
}

/**
 * 查询单个老师数据 展示当前审核和未审核数量
 */
export async function queryVolunteerTeacherSingle(param: RP) {
  const res = await request.post<R<TeacherInfo>>(
    `${BASE_URL}/volunteer/single`,
    param
  );
  return res.data.code === 0 ? res.data.data : Promise.reject(res.data);
}

/**
 * 查询教师基础信息（用于下拉选择，账号、姓名、职称、学院、系所、教研室、专业）
 */
export async function queryTeachersBaseInfo(param: RP) {
  return LzdAjax.post<TenantTeacher[]>(
    `${BASE_URL}/manager/query/list/basic-info`,
    param,
    false,
    true
  ).then((v) => v.data!);
}

/**
 * 查询教师基础信息（用于下拉选择，账号、姓名、职称、学院、系所、教研室、专业）
 */
export async function queryTeachersBaseInfoBy(
  data: Partial<TenantTeacher> = {}
) {
  return queryTeachersBaseInfo({ where: RParam.buildWhere(data) });
}

/** 根据 userid 查询教师信息  */
async function queryByUserid(userid: string) {
  return listOneTenantTeacher({ where: LzdUtil.buildWhere({ userid }) });
}

/** 更新教师通过userid */
async function teacherUpdateByUserid(data: TenantTeacher) {
  if (!data.userid) {
    return LzdUtil.tipsParameterError();
  }
  return LzdAjax.post<unknown>(
    BASE_URL + '/teacher/update',
    {
      where: RParam.buildWhere({ userid: data.userid }),
      update: RParam.buildEdit(
        data,
        (k: string) => k !== 'seq' && k !== 'userid'
      )
    },
    false,
    true
  ).then((v) => v.data ?? []);
}

/**修改老师字段 */

export async function updateTeachersFileds(param: RP) {
  const res = await request.post<R<any>>(
    '/bsl/tenant/teachers/manager/update/fields',
    param
  );
  return res.data.code === 0 ? res.data.message : Promise.reject(res.data);
}

/** 教师API */
class tenantTeacherApi {
  static queryPage = pageTenantTeacher;
  static queryByUserid = queryByUserid;
  static teacherUpdateByUserid = teacherUpdateByUserid;
}

export default tenantTeacherApi;
